import { useThrottleFn } from '@vueuse/core';
import { markRaw, onBeforeUnmount, ref, type Ref } from 'vue';
import { bindChipTap } from './bindChipTap';
import { createChipElement, patchChipElement } from './createChipElement';
import { loadYandexMaps } from './loadYandexMaps';
import type { MapCamera, MapCluster, MapHotel, MapMargin, MapMarkerView, MapViewportSize } from './types';
import type { YMapCameraState, YMapInstance, YMapMarkerInstance, Ymaps3 } from './ymaps';
import {
  CAMERA_THROTTLE_MS,
  CLUSTER_FLY_DURATION_MS,
  FIRST_FLY_DURATION_MS,
  FIRST_FLY_ZOOM_BACKOFF,
  HOTELS_MAP_LAYER_Z_INDEX,
  HOTELS_MAP_SOURCE_ID,
  MAP_COPYRIGHTS_POSITION,
  MAP_DISTRIBUTION_POSITION,
  MAP_MARGIN,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MAP_ZOOM_ROUNDING,
  MARKER_HIDE_EXTENT_PX,
  PENDING_TAP_TIMEOUT_MS,
  SELECT_FLY_DURATION_MS,
} from '../config/map';
import { getClusterZoomKey, clusterHotels } from '../lib/clusterHotels';
import { getClusterCameraAction, getFirstFitCamera } from '../lib/mapBounds';
import { diffMarkerViews, toMarkerViews } from '../lib/markerViews';
import { toLngLat } from '../lib/mercator';

interface MarkerEntry {
  marker: YMapMarkerInstance;
  element: HTMLElement;
  view: MapMarkerView;
}

export interface HotelsMapMarkersOptions {
  container: Ref<HTMLElement | null>;
  getHotels: () => MapHotel[];
  selectedHotelId: Ref<number | null>;
  onSelect: (hotelId: number) => void;
  onExpand: (hotels: MapHotel[]) => void;
}

export interface HotelsMapMarkers {
  isReady: Ref<boolean>;
  hasError: Ref<boolean>;
  isEmpty: Ref<boolean>;
  mount: () => Promise<void>;
  refresh: () => void;
  refreshSelection: () => void;
  flyToHotel: (hotel: MapHotel) => void;
  setMargin: (margin: [number, number, number, number]) => void;
}

export function useHotelsMapMarkers(options: HotelsMapMarkersOptions): HotelsMapMarkers {
  const isReady = ref(false);
  const hasError = ref(false);
  const isEmpty = ref(false);

  const markers = new Map<number, MarkerEntry>();

  let ymaps3: Ymaps3 | null = null;
  let map: YMapInstance | null = null;
  let viewport: MapViewportSize = { width: 0, height: 0 };
  let clusters: MapCluster[] = [];
  let views: MapMarkerView[] = [];
  let zoomKey: number | null = null;
  let activeMargin: MapMargin = MAP_MARGIN;
  let resizeObserver: ResizeObserver | null = null;
  let mounting: Promise<void> | null = null;
  let isDestroyed = false;
  let isMapInAction = false;
  let pendingTapKey: number | null = null;
  let pendingTapTimer = 0;

  function applyViews(next: MapMarkerView[]): void {
    if (!map || !ymaps3) return;

    const diff = diffMarkerViews(views, next);

    diff.removed.forEach((key) => {
      const entry = markers.get(key);

      if (!entry) return;

      map?.removeChild(entry.marker);
      markers.delete(key);
    });

    diff.updated.forEach((view) => {
      const entry = markers.get(view.key);

      if (!entry) return;

      patchChipElement(entry.element, entry.view, view);

      if (
        entry.view.coordinates[0] !== view.coordinates[0] ||
        entry.view.coordinates[1] !== view.coordinates[1] ||
        entry.view.zIndex !== view.zIndex
      ) {
        entry.marker.update({ coordinates: view.coordinates, zIndex: view.zIndex });
      }

      entry.view = view;
    });

    diff.added.forEach((view) => {
      const element = createChipElement(view);

      bindChipTap(element, () => onMarkerTap(view.key));

      const marker = markRaw(
        new (ymaps3 as Ymaps3).YMapMarker(
          {
            coordinates: view.coordinates,
            source: HOTELS_MAP_SOURCE_ID,
            zIndex: view.zIndex,
            properties: { key: view.key },
            hideOutsideViewport: { extent: MARKER_HIDE_EXTENT_PX },
          },
          element,
        ),
      );

      map?.addChild(marker);
      markers.set(view.key, { marker, element, view });
    });

    views = next;
  }

  function render(nextZoomKey: number): void {
    zoomKey = nextZoomKey;
    clusters = clusterHotels(options.getHotels(), { zoom: nextZoomKey });
    applyViews(toMarkerViews(clusters, options.selectedHotelId.value));
  }

  function onMarkerClick(key: number): void {
    const cluster = clusters.find((candidate) => candidate.key === key);

    if (!cluster) return;

    if (cluster.hotels.length === 1) {
      const [hotel] = cluster.hotels;

      if (options.selectedHotelId.value === hotel.hotelId) flyToHotel(hotel);
      else options.onSelect(hotel.hotelId);

      return;
    }

    const action = getClusterCameraAction(cluster, viewport, activeMargin, zoomKey ?? MAP_MIN_ZOOM);

    if (action.kind === 'list') {
      options.onExpand(action.hotels);
      return;
    }

    render(getClusterZoomKey(action.camera.zoom));

    map?.setLocation({
      center: toLngLat(action.camera.center),
      zoom: action.camera.zoom,
      duration: CLUSTER_FLY_DURATION_MS,
      easing: 'ease-in-out',
    });
  }

  function onMarkerTap(key: number): void {
    if (!isMapInAction) {
      onMarkerClick(key);
      return;
    }

    pendingTapKey = key;
    window.clearTimeout(pendingTapTimer);
    pendingTapTimer = window.setTimeout(flushPendingTap, PENDING_TAP_TIMEOUT_MS);
  }

  function flushPendingTap(): void {
    window.clearTimeout(pendingTapTimer);

    const key = pendingTapKey;

    pendingTapKey = null;

    if (key !== null) onMarkerClick(key);
  }

  const onCameraUpdate = useThrottleFn(
    (event: { location: { zoom: number }; mapInAction: boolean }): void => {
      if (event.mapInAction) return;

      const nextZoomKey = getClusterZoomKey(event.location.zoom);

      if (nextZoomKey === zoomKey) return;

      render(nextZoomKey);
    },
    CAMERA_THROTTLE_MS,
    true,
  );

  function onMapUpdate(event: YMapCameraState): void {
    isMapInAction = event.mapInAction;

    if (!event.mapInAction) flushPendingTap();

    onCameraUpdate(event);
  }

  function mount(): Promise<void> {
    mounting =
      mounting ??
      createMap().finally(() => {
        mounting = null;
      });

    return mounting;
  }

  function flyToCamera(camera: MapCamera): void {
    render(getClusterZoomKey(camera.zoom));

    map?.setLocation({
      center: toLngLat(camera.center),
      zoom: camera.zoom,
      duration: FIRST_FLY_DURATION_MS,
      easing: 'ease-out',
    });
  }

  async function createMap(): Promise<void> {
    try {
      ymaps3 = markRaw(await loadYandexMaps());
    } catch {
      hasError.value = true;
      return;
    }

    const container = options.container.value;

    if (isDestroyed || map || !container) return;

    measureViewport(container);
    observeResize(container);

    const hotels = options.getHotels();
    const camera = getFirstFitCamera(hotels, viewport, activeMargin);

    if (!camera) {
      isEmpty.value = true;
      isReady.value = true;
      return;
    }

    isEmpty.value = false;

    map = markRaw(
      new ymaps3.YMap(container, {
        location: {
          center: toLngLat(camera.center),
          zoom: Math.max(camera.zoom - FIRST_FLY_ZOOM_BACKOFF, MAP_MIN_ZOOM),
        },
        margin: activeMargin,
        behaviors: ['drag', 'pinchZoom', 'scrollZoom', 'dblClick'],
        copyrightsPosition: MAP_COPYRIGHTS_POSITION,
        distributionPosition: MAP_DISTRIBUTION_POSITION,
        zoomRounding: MAP_ZOOM_ROUNDING,
        zoomRange: { min: MAP_MIN_ZOOM, max: MAP_MAX_ZOOM },
      }),
    );

    map.addChild(new ymaps3.YMapDefaultSchemeLayer({}));
    map.addChild(new ymaps3.YMapFeatureDataSource({ id: HOTELS_MAP_SOURCE_ID }));
    map.addChild(
      new ymaps3.YMapLayer({ source: HOTELS_MAP_SOURCE_ID, type: 'markers', zIndex: HOTELS_MAP_LAYER_Z_INDEX }),
    );
    map.addChild(markRaw(new ymaps3.YMapListener({ onUpdate: onMapUpdate })));

    flyToCamera(camera);

    isReady.value = true;
  }

  function refresh(): void {
    if (!map) {
      if (options.getHotels().length) mount();
      return;
    }

    isEmpty.value = !getFirstFitCamera(options.getHotels(), viewport, activeMargin);

    render(zoomKey ?? MAP_MIN_ZOOM);
  }

  function refreshSelection(): void {
    if (!map) return;

    applyViews(toMarkerViews(clusters, options.selectedHotelId.value));
  }

  function flyToHotel(hotel: MapHotel): void {
    map?.setLocation({
      center: toLngLat(hotel.coords),
      duration: SELECT_FLY_DURATION_MS,
      easing: 'ease-out',
    });
  }

  function setMargin(margin: MapMargin): void {
    activeMargin = margin;
    map?.setMargin(margin);
  }

  function measureViewport(container: HTMLElement): void {
    const rect = container.getBoundingClientRect();

    viewport = { width: rect.width, height: rect.height };
  }

  function observeResize(container: HTMLElement): void {
    if (resizeObserver || typeof ResizeObserver === 'undefined') return;

    resizeObserver = new ResizeObserver(() => measureViewport(container));
    resizeObserver.observe(container);
  }

  onBeforeUnmount(() => {
    window.clearTimeout(pendingTapTimer);
    isDestroyed = true;
    resizeObserver?.disconnect();
    resizeObserver = null;
    markers.clear();
    views = [];
    map?.destroy();
    map = null;
  });

  return { isReady, hasError, isEmpty, mount, refresh, refreshSelection, flyToHotel, setMargin };
}
